import React, { useMemo, useState } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { FishsixParentReviewDetail } from "@/components/admin/FishsixParentReviewDetail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import { averageFishsixSurveyScore, parseFishsixV1Responses } from "@/utils/fishsixSurveyAdmin";
import { FISHSIX_SURVEY_VERSION } from "@/utils/fishsixSurveyConstants";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Eye, Loader2, Search, Star, Users, MessageSquare, GraduationCap } from "lucide-react";

type ParentReview = Database["public"]["Tables"]["parent_reviews"]["Row"] & {
  student_type?: string | null;
  survey_version?: string | null;
  student_code?: string | null;
  responses?: any;
  review_kind?: string | null;
  teacher_id?: string | null;
};

type ReviewKindFilter = "all" | "general" | "teacher";

function reviewKindOf(r: ParentReview): "general" | "teacher" {
  // Backward compat: NULL → general
  return r.review_kind === "teacher" ? "teacher" : "general";
}

function isFishsixV1Row(r: ParentReview): boolean {
  return r.student_type === "fishsix" && r.survey_version === FISHSIX_SURVEY_VERSION && r.responses != null;
}

function isLegacyStarRow(r: ParentReview): boolean {
  return r.rating != null && r.rating >= 1 && r.rating <= 5;
}

function matchesSearch(r: ParentReview, q: string): boolean {
  if (!q.trim()) return true;
  const s = q.toLowerCase();
  return (
    (r.parent_line_name?.toLowerCase().includes(s) ?? false) ||
    r.parent_line_id.toLowerCase().includes(s) ||
    (r.feedback?.toLowerCase().includes(s) ?? false) ||
    (r.student_code?.toLowerCase().includes(s) ?? false) ||
    (r.teacher_id?.toLowerCase().includes(s) ?? false)
  );
}

function StarsSummary({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i <= rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/25"}`}
        />
      ))}
      <span className="ml-1 text-xs tabular-nums text-muted-foreground">{rating}/5</span>
    </div>
  );
}

function KindBadge({ kind }: { kind: "general" | "teacher" }) {
  return kind === "teacher" ? (
    <Badge className="bg-primary/10 text-primary hover:bg-primary/15 font-normal">ครู</Badge>
  ) : (
    <Badge variant="secondary" className="font-normal">ทั่วไป</Badge>
  );
}

function summaryScore(r: ParentReview): number | null {
  if (!isFishsixV1Row(r)) return null;
  const parsed = parseFishsixV1Responses(r.responses);
  if (!parsed) return null;
  const kind = reviewKindOf(r);
  if (kind === "teacher") {
    return parsed.s2_teacher_quality >= 1 && parsed.s2_teacher_quality <= 10
      ? parsed.s2_teacher_quality
      : null;
  }
  // general: avg of s1, s3, s4
  const vals = [parsed.s1_academic_development, parsed.s3_admin_service, parsed.s4_facility_atmosphere].filter(
    (n) => n >= 1 && n <= 10,
  );
  if (!vals.length) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

function SummaryCell({ r }: { r: ParentReview }) {
  const score = summaryScore(r);
  if (score != null) {
    return <span className="text-sm font-medium tabular-nums">{score}/10</span>;
  }
  if (isFishsixV1Row(r)) {
    const parsed = parseFishsixV1Responses(r.responses);
    const avg = parsed ? averageFishsixSurveyScore(parsed) : null;
    if (avg != null) return <span className="text-sm tabular-nums">{avg}/10</span>;
  }
  if (isLegacyStarRow(r) && r.rating != null) return <StarsSummary rating={r.rating} />;
  return <span className="text-muted-foreground">—</span>;
}

interface TeacherLite {
  teacher_code: string;
  full_name: string;
  nickname: string;
}

function teacherLabel(id: string | null | undefined, list: TeacherLite[]): string {
  if (!id) return "—";
  const t = list.find((x) => x.teacher_code === id);
  if (!t) return id;
  const nick = t.nickname?.trim();
  return nick ? `${nick} (${t.full_name}) · ${id}` : `${t.full_name} · ${id}`;
}

const AdminParentReviewsPage: React.FC = () => {
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<ReviewKindFilter>("all");
  const [teacherFilter, setTeacherFilter] = useState<string>("all");
  const [teacherSearch, setTeacherSearch] = useState("");
  const [detailReview, setDetailReview] = useState<ParentReview | null>(null);

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["parent-reviews"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("parent_reviews")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as ParentReview[];
    },
  });

  const { data: teachers = [] } = useQuery({
    queryKey: ["teacher-profiles-lite"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("teacher_profiles_view")
        .select("teacher_code, full_name, nickname")
        .order("teacher_code");
      if (error) throw error;
      return (data ?? []) as TeacherLite[];
    },
  });

  // ===== Dashboard metrics =====
  const dashboard = useMemo(() => {
    const total = reviews.length;
    const general = reviews.filter((r) => reviewKindOf(r) === "general");
    const teacher = reviews.filter((r) => reviewKindOf(r) === "teacher");

    const avg = (rows: ParentReview[]) => {
      const vals = rows.map(summaryScore).filter((v): v is number => v != null);
      if (!vals.length) return null;
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
    };

    const teacherBreakdown = new Map<string, { count: number; sum: number; n: number }>();
    teacher.forEach((r) => {
      const id = r.teacher_id ?? "(unknown)";
      const cur = teacherBreakdown.get(id) ?? { count: 0, sum: 0, n: 0 };
      cur.count += 1;
      const s = summaryScore(r);
      if (s != null) {
        cur.sum += s;
        cur.n += 1;
      }
      teacherBreakdown.set(id, cur);
    });
    const teacherStats = Array.from(teacherBreakdown.entries())
      .map(([id, v]) => ({
        teacher_id: id,
        count: v.count,
        avg: v.n > 0 ? Math.round((v.sum / v.n) * 10) / 10 : null,
      }))
      .sort((a, b) => b.count - a.count);

    const uniqueParents = new Set(reviews.map((r) => r.parent_line_id)).size;

    return {
      total,
      generalCount: general.length,
      teacherCount: teacher.length,
      generalAvg: avg(general),
      teacherAvg: avg(teacher),
      teacherStats,
      uniqueParents,
    };
  }, [reviews]);

  // ===== Filtered list (table) =====
  const filtered = useMemo(() => {
    return reviews.filter((r) => {
      const k = reviewKindOf(r);
      if (kindFilter !== "all" && k !== kindFilter) return false;
      if (teacherFilter !== "all") {
        if (k !== "teacher" || r.teacher_id !== teacherFilter) return false;
      }
      if (!matchesSearch(r, search)) return false;
      return true;
    });
  }, [reviews, kindFilter, teacherFilter, search]);

  // ===== Group by student + month =====
  type Group = {
    key: string;
    student_code: string | null;
    parent_line_id: string;
    parent_line_name: string | null;
    monthLabel: string;
    monthDate: Date;
    general?: ParentReview;
    teachers: ParentReview[];
  };

  const groups: Group[] = useMemo(() => {
    const map = new Map<string, Group>();
    for (const r of filtered) {
      if (!r.created_at) continue;
      const d = new Date(r.created_at);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const studentKey = r.student_code ?? `parent:${r.parent_line_id}`;
      const key = `${studentKey}|${monthKey}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          student_code: r.student_code ?? null,
          parent_line_id: r.parent_line_id,
          parent_line_name: r.parent_line_name ?? null,
          monthLabel: format(d, "MM/yyyy"),
          monthDate: new Date(d.getFullYear(), d.getMonth(), 1),
          teachers: [],
        };
        map.set(key, g);
      }
      if (reviewKindOf(r) === "teacher") g.teachers.push(r);
      else g.general = r;
    }
    return Array.from(map.values()).sort((a, b) => b.monthDate.getTime() - a.monthDate.getTime());
  }, [filtered]);

  const teacherOptions = useMemo(() => {
    const q = teacherSearch.trim().toLowerCase();
    if (!q) return teachers;
    return teachers.filter(
      (t) =>
        t.teacher_code.toLowerCase().includes(q) ||
        t.full_name.toLowerCase().includes(q) ||
        t.nickname.toLowerCase().includes(q),
    );
  }, [teachers, teacherSearch]);

  const detailParsedFishsix = detailReview ? parseFishsixV1Responses(detailReview.responses) : null;
  const detailKind = detailReview ? reviewKindOf(detailReview) : "general";
  const detailLegacy = detailReview != null && isLegacyStarRow(detailReview);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <SidebarInset>
          <div className="container mx-auto max-w-7xl px-4 py-8">
            <div className="mb-6 flex flex-col gap-2">
              <h1 className="text-2xl font-bold tracking-tight">แบบประเมินความพึงพอใจผู้ปกครอง</h1>
              <p className="text-sm text-muted-foreground">
                สรุปและรายละเอียดของแบบประเมิน — แยกหมวดทั่วไปและหมวดคุณภาพคุณครูรายคน
              </p>
            </div>

            {/* Dashboard */}
            <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">รวมทั้งหมด</CardTitle>
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold tabular-nums">{dashboard.total}</div>
                  <p className="text-xs text-muted-foreground mt-1">{dashboard.uniqueParents} ผู้ปกครอง</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">หมวดทั่วไป</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold tabular-nums">{dashboard.generalCount}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    เฉลี่ย {dashboard.generalAvg != null ? `${dashboard.generalAvg}/10` : "—"}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">หมวดคุณครู</CardTitle>
                  <GraduationCap className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold tabular-nums">{dashboard.teacherCount}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    เฉลี่ย {dashboard.teacherAvg != null ? `${dashboard.teacherAvg}/10` : "—"}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">ครูที่ถูกประเมิน</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold tabular-nums">{dashboard.teacherStats.length}</div>
                  <p className="text-xs text-muted-foreground mt-1">คน</p>
                </CardContent>
              </Card>
            </div>

            {/* Per-teacher breakdown */}
            {dashboard.teacherStats.length > 0 ? (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-base">สรุปคะแนนรายครู</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-card">
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-2 font-medium">ครู</th>
                          <th className="py-2 font-medium text-right">จำนวนรีวิว</th>
                          <th className="py-2 font-medium text-right">คะแนนเฉลี่ย</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.teacherStats.map((t) => (
                          <tr key={t.teacher_id} className="border-b last:border-0">
                            <td className="py-2">{teacherLabel(t.teacher_id, teachers)}</td>
                            <td className="py-2 text-right tabular-nums">{t.count}</td>
                            <td className="py-2 text-right tabular-nums">
                              {t.avg != null ? `${t.avg}/10` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* Filters */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as ReviewKindFilter)}>
                <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกหมวด</SelectItem>
                  <SelectItem value="general">หมวดทั่วไป</SelectItem>
                  <SelectItem value="teacher">หมวดคุณครู</SelectItem>
                </SelectContent>
              </Select>

              <Select value={teacherFilter} onValueChange={setTeacherFilter}>
                <SelectTrigger className="w-full sm:w-72"><SelectValue placeholder="กรองครู" /></SelectTrigger>
                <SelectContent>
                  <div className="p-2">
                    <Input
                      placeholder="ค้นหาครู..."
                      value={teacherSearch}
                      onChange={(e) => setTeacherSearch(e.target.value)}
                      className="h-8"
                    />
                  </div>
                  <SelectItem value="all">ทุกคน</SelectItem>
                  {teacherOptions.map((t) => (
                    <SelectItem key={t.teacher_code} value={t.teacher_code}>
                      {t.nickname?.trim() ? `${t.nickname} · ` : ""}{t.full_name} ({t.teacher_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="relative flex-1 sm:max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="ค้นหา ชื่อ Line, รหัสนักเรียน, รหัสครู..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Grouped list */}
            <div className="space-y-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : groups.length === 0 ? (
                <div className="rounded-xl border bg-card py-16 text-center text-muted-foreground">
                  ไม่พบข้อมูล
                </div>
              ) : (
                groups.map((g) => (
                  <Card key={g.key}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-base">
                            {g.parent_line_name || "ผู้ปกครอง"}
                            {g.student_code ? (
                              <span className="ml-2 font-mono text-xs text-muted-foreground">
                                {g.student_code}
                              </span>
                            ) : null}
                          </CardTitle>
                          <p className="mt-1 text-xs text-muted-foreground font-mono">
                            Line: {g.parent_line_id}
                          </p>
                        </div>
                        <Badge variant="outline" className="font-normal">เดือน {g.monthLabel}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {g.general ? (
                        <RowItem r={g.general} teachers={teachers} onOpen={setDetailReview} />
                      ) : null}
                      {g.teachers.map((t) => (
                        <RowItem key={t.id} r={t} teachers={teachers} onOpen={setDetailReview} />
                      ))}
                      {!g.general && g.teachers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">ไม่มีรายการ</p>
                      ) : null}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            <p className="mt-4 text-xs text-muted-foreground">
              แสดง {groups.length} กลุ่ม · {filtered.length} รายการ
            </p>
          </div>
        </SidebarInset>
      </div>

      <Sheet open={detailReview != null} onOpenChange={(open) => !open && setDetailReview(null)}>
        <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-2xl">
          {detailReview ? (
            <>
              <SheetHeader className="space-y-3 border-b pb-4 text-left">
                <SheetTitle className="pr-8 text-xl leading-snug">
                  {detailReview.parent_line_name || "ผู้ปกครอง"}
                </SheetTitle>
                <SheetDescription asChild>
                  <div className="space-y-2 text-left text-sm text-muted-foreground">
                    <div className="flex flex-wrap gap-2">
                      <KindBadge kind={detailKind} />
                      {detailReview.survey_version ? (
                        <Badge variant="outline" className="font-mono font-normal">
                          {detailReview.survey_version}
                        </Badge>
                      ) : null}
                      {detailReview.student_type ? (
                        <Badge variant="outline" className="font-normal">
                          {detailReview.student_type}
                        </Badge>
                      ) : null}
                    </div>
                    <dl className="grid gap-1 text-xs sm:text-sm">
                      <div className="flex gap-2">
                        <dt className="min-w-[7rem] shrink-0 text-muted-foreground">Line ID</dt>
                        <dd className="font-mono text-foreground">{detailReview.parent_line_id}</dd>
                      </div>
                      {detailReview.student_code ? (
                        <div className="flex gap-2">
                          <dt className="min-w-[7rem] shrink-0 text-muted-foreground">รหัสนักเรียน</dt>
                          <dd className="font-mono text-foreground">{detailReview.student_code}</dd>
                        </div>
                      ) : null}
                      {detailReview.teacher_id ? (
                        <div className="flex gap-2">
                          <dt className="min-w-[7rem] shrink-0 text-muted-foreground">ครูที่ประเมิน</dt>
                          <dd className="text-foreground">{teacherLabel(detailReview.teacher_id, teachers)}</dd>
                        </div>
                      ) : null}
                      <div className="flex gap-2">
                        <dt className="min-w-[7rem] shrink-0 text-muted-foreground">บันทึกเมื่อ</dt>
                        <dd className="text-foreground">
                          {detailReview.created_at ? format(new Date(detailReview.created_at), "dd/MM/yyyy HH:mm") : "—"}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </SheetDescription>
              </SheetHeader>

              {isFishsixV1Row(detailReview) && detailParsedFishsix ? (
                <FishsixParentReviewDetail parsed={detailParsedFishsix} scope={detailKind} />
              ) : detailLegacy ? (
                <div className="mt-6 space-y-6 pb-8">
                  <div>
                    <p className="mb-2 text-sm font-medium text-foreground">คะแนนดาว</p>
                    {detailReview.rating != null ? (
                      <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-4 py-3">
                        <StarsSummary rating={detailReview.rating} />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-medium text-foreground">ความคิดเห็น</p>
                    {detailReview.feedback?.trim() ? (
                      <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap">
                        {detailReview.feedback}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">ไม่มีข้อความ</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-6 space-y-4 pb-8">
                  <p className="text-sm font-medium text-foreground">ข้อมูลแบบสอบถาม (ดิบ)</p>
                  <pre className="max-h-[60vh] overflow-auto rounded-lg border bg-muted/40 p-4 text-xs leading-relaxed">
                    {detailReview.responses != null
                      ? JSON.stringify(detailReview.responses, null, 2)
                      : "ไม่มี responses"}
                  </pre>
                </div>
              )}
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </SidebarProvider>
  );
};

function RowItem({
  r,
  teachers,
  onOpen,
}: {
  r: ParentReview;
  teachers: TeacherLite[];
  onOpen: (r: ParentReview) => void;
}) {
  const kind = reviewKindOf(r);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2">
      <KindBadge kind={kind} />
      {kind === "teacher" ? (
        <span className="text-sm font-medium">{teacherLabel(r.teacher_id, teachers)}</span>
      ) : (
        <span className="text-sm text-muted-foreground">หมวดทั่วไป (ส่วนที่ 1, 3, 4, 5)</span>
      )}
      <div className="ml-auto flex items-center gap-3">
        <SummaryCell r={r} />
        <span className="text-xs text-muted-foreground tabular-nums">
          {r.created_at ? format(new Date(r.created_at), "dd/MM HH:mm") : "—"}
        </span>
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => onOpen(r)}>
          <Eye className="h-3.5 w-3.5" />
          เปิด
        </Button>
      </div>
    </div>
  );
}

export default AdminParentReviewsPage;
